import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import { communityActions } from 'community/api';
import { selectActiveCommunityId } from 'community/state/selectors';
import i18n from 'i18n';
import { addAppShareDialog } from 'modules/alerts/state';
import { isApiResponse } from 'modules/api/utils';
import { getNatterPublicPath } from 'natters/routes';
import type { RootState } from 'store';
import { handleQueryError } from 'store/utils';

import { nattersApi } from './nattersApi';
import type { PostAvailabilityPayload } from './types';

export const nattersApiLister = createListenerMiddleware<RootState>();

nattersApiLister.startListening({
  matcher: isAnyOf(
    nattersApi.endpoints.getNattersList.matchRejected,
    nattersApi.endpoints.createNatter.matchRejected,
    nattersApi.endpoints.getNatter.matchRejected,
    nattersApi.endpoints.getPublicNatter.matchRejected,
    nattersApi.endpoints.postAvailability.matchRejected
  ),
  effect: ({ type, payload }, { dispatch }) => {
    const response = isApiResponse(payload) ? payload : undefined;
    handleQueryError(payload, dispatch, type, response?.data || 'Unable to call Natters API');
  },
});

nattersApiLister.startListening({
  matcher: nattersApi.endpoints.createNatter.matchFulfilled,
  effect: async ({ payload }, { dispatch }) => {
    const natterUrl = getNatterPublicPath(payload.id, true);
    navigator.clipboard.writeText(natterUrl);
    dispatch(
      addAppShareDialog({
        title: i18n.t('natters:createdConfirmation.title'),
        description: i18n.t('natters:createdConfirmation.description'),
        confirmLabel: i18n.t('common:done'),
        shareOptions: {
          url: natterUrl,
        },
        centered: true,
      })
    );
  },
});

nattersApiLister.startListening({
  matcher: nattersApi.endpoints.postAvailability.matchFulfilled,
  effect: async ({ meta }, { dispatch, getState }) => {
    const currentActiveCommunityId = selectActiveCommunityId(getState());

    // Only set if not already set (for new sign-ups)
    if (!currentActiveCommunityId) {
      const { id: openNatterId } = meta.arg.originalArgs as PostAvailabilityPayload;
      const { communityId } = await dispatch(nattersApi.endpoints.getNatter.initiate(openNatterId)).unwrap();

      dispatch(
        communityActions.community.registeredToCommunity({
          communityId,
          showMemberSetupDialog: false, // MemberSetup is inline in NatterAvailability
        })
      );
    }
  },
});
